import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { AiOutlineArrowRight } from "react-icons/ai";
import { useChat } from '../context/ChatProvider';
import { useTranslation } from 'react-i18next';
import i18n from "../i18n/i18n";
import axios from 'axios';

const LoginWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh; /* Full viewport height */
    background: var(--background-color); /* Define a background color in your CSS variables */
    padding: 20px;
`;

const Logo = styled.img`
    width: 250px;
    height: auto;
    margin-bottom: 20px;

    @media (max-width: 820px) {
        width: 250px;
    }
`;

const LoginContainer = styled.form`
    display: flex;
    flex-direction: column;
    gap: 15px;
    background: #fff;
    padding: 25px 30px;
    border-radius: 15px;
    width: clamp(350px, 25vw, 300px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    @media (max-width: 820px) {
        width: 90%;
        padding: 20px;
    }
`;

const sharedInputStyles = css`
    width: 100%;
    padding: 10px 15px;
    border: 2px solid #ddd;
    border-radius: 8px;
    font-size: 1em;
    transition: border-color 0.3s;

    &::placeholder {
        color: #7b7b7b;
        font-size: 1em;
    }

    &:focus {
        outline: none;
        border-color: var(--blue-active-color);
    }

    @media (max-width: 820px) {
        font-size: 0.9em;
    }
`;

const WelcomeMessage = styled.p`
    margin: auto 0;
    font-size: 0.9em;
    text-align: center;
    color: rgba(0, 0, 0, 0.5);
`;

const ErrorMessage = styled.p`
    color: red;
    font-size: 0.8em;
    text-align: center;
`;

const LoadingMessage = styled.p`
    color: var(--blue-button-color);
    font-size: 0.8em;
    text-align: center;
`;

const Input = styled.input.attrs(() => ({
    type: 'text'
}))`
    ${sharedInputStyles}
`;

const Select = styled.select`
    ${sharedInputStyles}
    background: #fff;
    color: #000;
    cursor: pointer;
`;

const SubmitButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 0;
    border: none;
    border-radius: 8px;
    background: var(--blue-active-color);
    transition: 0.3s ease-in-out opacity, box-shadow;
    cursor: pointer;
    font-size: 1em;
    color: #fff;

    &:hover {
        opacity: 0.85;
        box-shadow: 0 8px 15px rgba(0, 0, 0, 0.3);
    }

    &:focus {
        outline: none;
    }

    @media (max-width: 820px) {
        font-size: 0.9em;
    }
`;

const Impressum = styled.a`
    margin-top: 20px;
    font-size: 0.8em;
    color: #555;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
        color: var(--blue-active-color);
    }

    @media (max-width: 820px) {
        font-size: 0.7em;
    }
`;

const Login = () => {
    const inputRef = useRef(null);
    const { setUsername, setLanguage, language } = useChat();
    const { t } = useTranslation();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const validUsernames = ["BITFLINXUAB", "UAB"];
    const apiUrl = process.env.REACT_APP_STARTUP_API;

    const handleLanguageChange = (e) => {
        const newLang = e.target.value;
        setLanguage(newLang);
        i18n.changeLanguage(newLang);
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent default form submission

        const enteredUsername = inputRef.current.value.trim();
        if (!validUsernames.includes(enteredUsername)) {
            setError(t('login.invalidUsername') || "Invalid username. Please try again.");
            return;
        }

        setError('');
        setLoading(true);

        try {
            // Send a request to the API to wake up the server
            const response = await axios.post(apiUrl, { message: "Wake up" }, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 200) {
                setUsername(enteredUsername); // Set the username in context
            } else {
                setError(t('login.serverError') || "Unable to contact the server. Please try again.");
            }
        } catch (error) {
            setError(t('login.serverError') || "Unable to contact the server. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <LoginWrapper>
            <Logo src={'./images/logo.svg'} alt="BITFLINX Logo" />
            <LoginContainer onSubmit={handleSubmit}>
                <WelcomeMessage>
                    {t('login.instruction') || 'Please use the username provided to you for testing purposes.'}
                </WelcomeMessage>

                <Input
                    placeholder={t('login.enterUsername') || "Enter a username"}
                    ref={inputRef}
                    aria-label={t('login.enterUsername') || "Enter a username"}
                    required
                />

                <Select
                    id="language-select"
                    value={language}
                    onChange={handleLanguageChange}
                    aria-label={t('language.select') || "Select Language"}
                >
                    <option value="en">{t('language.english') || 'English'}</option>
                    <option value="de">{t('language.german') || 'Deutsch'}</option>
                    <option value="ro">{t('language.romanian') || 'Romanian'}</option>
                    <option value="ar">{t('language.arabic') || 'Arabic'}</option>
                </Select>

                <SubmitButton type="submit" aria-label={t('login.submit') || "Submit Username"}>
                    <AiOutlineArrowRight color='#fff' size="1.2em" />
                </SubmitButton>

                {error && <ErrorMessage>{error}</ErrorMessage>}
                {loading && <LoadingMessage>{t('login.loading') || "Connecting to the server, please wait..."}</LoadingMessage>}
            </LoginContainer>

            <Impressum href="https://www.bitflinx.com/impressum/" target="_blank" rel="noopener noreferrer">
                {t('impressum.title') || "Impressum"}
            </Impressum>
        </LoginWrapper>
    );
};

export default Login;
